import {
  ApiDashboardModelSpiderChartDataRetrieveLimitEnum,
  DashboardDoughnutModel,
  DashboardDoughnutUseCase,
} from '@api';
import { Dropdown, DropdownItemType } from '@components/ui';
import { useApi } from '@data-access';
import { MapperHelper, chartColors } from '@helpers';
import { indicatorsLabels } from '@lib';
import { notionStore } from '@stores/notion.store';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartOptions } from 'chart.js';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { DashboardApiFilterParameters } from 'src/pages/dashboard';

import { ChartCard } from '../ChartCard/ChartCard';

import styles from './DoughnutChart.module.scss';

ChartJS.register(ArcElement, Tooltip, Legend);

function label(context: any) {
  return `${context.dataset.label}: ${MapperHelper.roundNumber(context.parsed * 100, 2)}%`;
}

export const options: ChartOptions<'doughnut'> = {
  layout: {
    padding: { top: 25 },
  },
  devicePixelRatio: 2.5,
  plugins: {
    title: {
      display: false,
      text: 'Risk indicators',
    },
    legend: {
      position: 'bottom' as any,
      labels: { pointStyle: 'rectRounded', usePointStyle: true, padding: 20 },
    },
    tooltip: {
      callbacks: {
        label,
      },
    },
    datalabels: {
      formatter: (value, ctx) => {
        let sum = 0;
        const dataArr = ctx.chart.data.datasets[0].data;
        dataArr.forEach((data: any) => {
          sum += data;
        });

        let percentage;
        if (value) {
          percentage = `${((value * 100) / sum).toFixed(2)}%`;
        } else {
          percentage = '';
        }
        return percentage;
      },
      color: '#fff',
    },
  },

  responsive: true,
  maintainAspectRatio: false,
};

const dropdownItems: DropdownItemType[] = [
  { label: 'Models', value: 'models' },
  { label: 'Use cases', value: 'useCases' },
];

export interface DatasetType {
  data: number[];
  label: string;
  backgroundColor: string[];
  hoverBackgroundColor: string[];
  borderColor: string[];
  borderWidth: number;
}

interface DoughnutProbProps {
  appliedFilters: DashboardApiFilterParameters;
  fixedWidth?: number;
}

export const DoughnutChartProb: React.FC<DoughnutProbProps> = observer(({ appliedFilters, fixedWidth }) => {
  const { dashboardApi } = useApi();

  const [models, setModels] = useState<DashboardDoughnutModel[]>();
  const [modelsRest, setModelsRest] = useState<number>();

  const [useCases, setUseCases] = useState<DashboardDoughnutUseCase[]>();
  const [useCasesRest, setUseCasesRest] = useState<number>();

  const [selectedType, setSelectedType] = useState<DropdownItemType>({ value: 'models', label: 'Models' });

  // set default
  const [modelQuantity, setModelQuantity] = useState<DropdownItemType>();
  const [useCaseQuantity, setUseCaseQuantity] = useState<DropdownItemType>();
  const [maxCountModels, setMaxCountModels] = useState<number>(0);
  const [maxCountUseCases, setMaxCountUseCases] = useState<number>(0);

  const getModels = useCallback(async () => {
    const {
      results: fetchedModels,
      restValue,
      maxCount,
    } = await dashboardApi.apiDashboardModelDoughnutChartDataRetrieve({
      ...appliedFilters,
      limit: ApiDashboardModelSpiderChartDataRetrieveLimitEnum.NUMBER_6,
    });
    setModelsRest(restValue);
    setModels(fetchedModels);
    setMaxCountModels(maxCount);
  }, [dashboardApi, appliedFilters]);

  const getModelsByLimit = useCallback(
    async (limit: ApiDashboardModelSpiderChartDataRetrieveLimitEnum) => {
      const { results: fetchedModels, restValue } = await dashboardApi.apiDashboardModelDoughnutChartDataRetrieve({
        ...appliedFilters,
        limit,
      });
      setModelsRest(restValue);
      setModels(fetchedModels);
    },
    [dashboardApi, appliedFilters],
  );

  useEffect(() => {
    if (!modelQuantity?.value) return;
    getModelsByLimit(modelQuantity.value as ApiDashboardModelSpiderChartDataRetrieveLimitEnum);
  }, [modelQuantity?.value, getModelsByLimit]);

  const getUseCasesByLimit = useCallback(
    async (limit: ApiDashboardModelSpiderChartDataRetrieveLimitEnum) => {
      const { results: fetchedUsecases, restValue } = await dashboardApi.apiDashboardUseCasesDoughnutChartDataRetrieve({
        ...appliedFilters,
        limit,
      });
      setUseCasesRest(restValue);
      setUseCases(fetchedUsecases);
    },
    [dashboardApi, appliedFilters],
  );

  useEffect(() => {
    if (!useCaseQuantity?.value) return;
    getUseCasesByLimit(useCaseQuantity.value as ApiDashboardModelSpiderChartDataRetrieveLimitEnum);
  }, [useCaseQuantity?.value, getUseCasesByLimit]);

  useEffect(() => {
    getModels();
  }, [getModels]);

  const getUsecases = useCallback(async () => {
    const {
      results: fethcedUsecases,
      restValue,
      maxCount,
    } = await dashboardApi.apiDashboardUseCasesDoughnutChartDataRetrieve({
      ...appliedFilters,
      limit: ApiDashboardModelSpiderChartDataRetrieveLimitEnum.NUMBER_6,
    });
    setUseCasesRest(restValue);
    setUseCases(fethcedUsecases);
    setMaxCountUseCases(maxCount);
  }, [dashboardApi, appliedFilters]);

  useEffect(() => {
    getUsecases();
  }, [getUsecases]);

  const modelsData = useMemo(() => {
    if (!models) return { labels: [], datasets: [] };
    const datasets: DatasetType[] = [
      {
        data: [],
        label: 'Total Probability',
        backgroundColor: [],
        hoverBackgroundColor: [],
        borderColor: [],
        borderWidth: 1,
      },
    ];

    const modelsLabels = [];
    for (let i = 0; i < models.length; i += 1) {
      const model = models[i];
      const colorKey = indicatorsLabels[i % indicatorsLabels.length].toLowerCase();
      datasets[0].data.push(model.totalValue);
      datasets[0].backgroundColor.push(chartColors[colorKey].backgroundColor);
      datasets[0].hoverBackgroundColor.push(chartColors[colorKey].hoverBackgroundColor);
      datasets[0].borderColor.push(chartColors[colorKey].borderColor);
      modelsLabels.push(models[i].aiModel);
    }
    if (modelsRest) {
      datasets[0].data.push(modelsRest);
      datasets[0].backgroundColor.push('#8d99aed9');
      datasets[0].hoverBackgroundColor.push('#8d99aef1');
      datasets[0].borderColor.push('#8d99aebf');
      modelsLabels.push('Rest');
    }

    return { labels: modelsLabels, datasets };
  }, [models, modelsRest]);

  const useCasesData = useMemo(() => {
    if (!useCases) return { labels: [], datasets: [] };
    const datasets: DatasetType[] = [
      {
        data: [],
        label: 'Total Probability',
        backgroundColor: [],
        hoverBackgroundColor: [],
        borderColor: [],
        borderWidth: 1,
      },
    ];

    const useCasesLabels = [];
    for (let i = 0; i < useCases.length; i += 1) {
      const useCase = useCases[i];
      const colorKey = indicatorsLabels[i % indicatorsLabels.length].toLowerCase();
      datasets[0].data.push(useCase.totalValue);
      datasets[0].backgroundColor.push(chartColors[colorKey].backgroundColor);
      datasets[0].hoverBackgroundColor.push(chartColors[colorKey].hoverBackgroundColor);
      datasets[0].borderColor.push(chartColors[colorKey].borderColor);
      useCasesLabels.push(useCases[i].useCase);
    }
    if (useCasesRest) {
      datasets[0].data.push(useCasesRest);
      datasets[0].backgroundColor.push('#8d99aed9');
      datasets[0].hoverBackgroundColor.push('#8d99aef1');
      datasets[0].borderColor.push('#8d99aebf');
      useCasesLabels.push('Rest');
    }

    return { labels: useCasesLabels, datasets };
  }, [useCases, useCasesRest]);

  const { values } = notionStore;

  const quantityArray = useMemo(() => {
    const numbers = [];
    let limit = selectedType.value === 'models' ? maxCountModels : maxCountUseCases;
    if (limit > 6) limit = 6;
    for (let i = 0; i < limit; i += 1) {
      numbers.push({ label: (i + 1).toString(), value: i + 1 });
    }

    setModelQuantity(
      maxCountModels > 6 ? { label: '6', value: 6 } : { label: maxCountModels.toString(), value: maxCountModels },
    );
    setUseCaseQuantity(
      maxCountUseCases > 6 ? { label: '6', value: 6 } : { label: maxCountUseCases.toString(), value: maxCountUseCases },
    );
    return numbers;
  }, [selectedType, maxCountModels, maxCountUseCases]);

  return (
    <ChartCard
      className={clsx(fixedWidth ? styles.reportCard : styles.dashboardCard)}
      title="Risk Contribution"
      subtitle="Risk assumption of the total risk of the riskiest models / use cases in the filtered portfolio"
      infoPosition="right"
      info={values?.doughnutChart || ' '}
    >
      <Dropdown
        className={styles.dropdown}
        onSelect={item => {
          setSelectedType(item);
        }}
        items={dropdownItems}
        selected={selectedType}
      />
      <Dropdown
        className={styles.dropdownQuantity}
        onSelect={item => {
          if (selectedType.value === 'models') {
            setModelQuantity(item);
          } else {
            setUseCaseQuantity(item);
          }
        }}
        items={quantityArray}
        selected={
          selectedType.value === 'models'
            ? modelQuantity || { label: '3', value: 3 }
            : useCaseQuantity || { label: '3', value: 3 }
        }
      />
      {fixedWidth ? (
        <Doughnut
          data={selectedType.value === 'models' ? modelsData : useCasesData}
          options={options}
          height={500}
          width={fixedWidth}
        />
      ) : (
        <div className={styles.chart}>
          <Doughnut
            data={selectedType.value === 'models' ? modelsData : useCasesData}
            options={options}
          />
        </div>
      )}
    </ChartCard>
  );
});
